<template>
  <div v-if="visible" class="cm-overlay" role="dialog" aria-modal="true" aria-label="Share via" @click.self="$emit('close')">
    <div class="cm-modal">
      <div class="cm-header">
        <h6 class="mb-0">Share “{{ title || 'Event' }}”</h6>
        <button class="cm-close float-right" @click="$emit('close')" aria-label="Close">×</button>
      </div>

      <div class="cm-body">
        <div class="d-flex align-items-start cm-summary">
          <img
            v-if="image"
            :src="image"
            alt="Event image"
            class="cm-cover mr-3"
          />
          <div class="flex-grow-1">
            <div class="font-weight-bold">{{ title || 'Event' }}</div>
            <div class="text-muted small" v-if="dates">{{ dates }}</div>
            <div class="text-muted small" v-if="location">
              <i class="fas fa-map-marker-alt mr-1"></i>{{ location }}
            </div>
            <div class="text-muted small" v-if="address">
              <i class="fas fa-home mr-1"></i>{{ address }}
            </div>
            <div class="text-muted small" v-if="mapHref">
              <i class="fas fa-map mr-1"></i>
              <a :href="mapHref" target="_blank" rel="noopener" class="cm-link">Open in Google Maps</a>
            </div>
            <p class="small mt-2 mb-0" v-if="description">{{ description }}</p>
          </div>
        </div>

        <hr />

        <!-- Actions -->
        <div class="cm-actions">
          <!-- Native share (mobile/tablet) -->
          <button
            type="button"
            class="btn btn-dark btn-sm"
            v-if="canNativeShare"
            @click="nativeShare"
          >
            <i class="fas fa-share-alt mr-1"></i> Share
          </button>

          <a
            class="btn btn-primary btn-sm"
            :href="facebookUrl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
          >
            <i class="fab fa-facebook-f mr-1"></i> Facebook
          </a>

          <a
            class="btn btn-info btn-sm"
            :href="linkedinUrl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
          >
            <i class="fab fa-linkedin-in mr-1"></i> LinkedIn
          </a>

          <a
            class="btn btn-success btn-sm"
            :href="whatsAppUrl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on WhatsApp"
          >
            <i class="fab fa-whatsapp mr-1"></i> WhatsApp
          </a>

          <!-- Instagram note: no official web share—encourage copy -->
          <button
            type="button"
            class="btn btn-danger btn-sm"
            @click="copyComposed"
            title="Instagram doesn't support web sharing; copy message and paste in Instagram."
            aria-label="Copy for Instagram"
          >
            <i class="fab fa-instagram mr-1"></i> Instagram
          </button>

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            @click="copyComposed"
            aria-label="Copy link and details"
          >
            <i class="far fa-copy mr-1"></i> Copy
          </button>
        </div>
      </div>

      <div class="cm-footer">
        <small class="text-muted">
          Tip: Instagram doesn’t support link sharing on web; copy the message and paste it into your bio/story.
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'
import Swal from "sweetalert2"
import "sweetalert2/dist/sweetalert2.min.css"

export default defineComponent({
  name: 'ShareViaModal',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '' },
    image: { type: String, default: '' },
    host: { type: String, default: '' },
    description: { type: String, default: '' },
    dates: { type: String, default: '' },
    datePrefix: { type: String, default: 'with time on' },
    location: { type: String, default: '' },
    address: { type: String, default: '' },
    mapUrl: { type: String, default: '' },
    url: { type: String, default: '' }
  },
  emits: ['close'],
  setup(props) {
    const DOWNLOAD_LINK = 'https://onelink.to/7ydjbn'
    const mapHref = computed(() => {
      if (props.mapUrl) return props.mapUrl
      const query = props.address || props.location
      if (!query) return ''
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    })
    const safeMapHref = computed(() => (mapHref.value === '#' ? '' : mapHref.value))

    // ---------- Message composition ----------
    const plainTitle = computed(() => props.title?.trim() || 'Event')
    const rsvpUrl = computed(() => props.url || window.location.href)
    const shareText = computed(() => {
      const parts = []
      const titleHost = props.host ? `${plainTitle.value} from ${props.host}` : plainTitle.value
      if (props.dates) parts.push(`${titleHost} for Multiple days ${props.datePrefix} ${props.dates}`)
      else parts.push(titleHost)
      const addr = props.address || props.location
      if (addr) parts.push(`at ${addr}`)
      if (safeMapHref.value) parts.push(safeMapHref.value)
      if (props.description) parts.push(props.description)
      if (rsvpUrl.value) parts.push(`Please RSVP using the link ${rsvpUrl.value}`)
      parts.push(`Download for Invi8 : ${DOWNLOAD_LINK}`)
      return parts.filter(Boolean).join(' ')
    })

    // Encoded for querystring usage
    const encodedUrl = computed(() => encodeURIComponent(rsvpUrl.value))
    const encodedShare = computed(() => encodeURIComponent(shareText.value))

    // ---------- Platform URLs ----------
    const facebookUrl = computed(() =>
      // FB reads OG tags from the URL; we pass only the URL.
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl.value}`
    )

    const linkedinUrl = computed(() =>
      // LinkedIn also mostly uses the URL for OG tags.
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl.value}`
    )

    const whatsAppUrl = computed(() => {
      // wa.me supports 'text=' with encoded content
      return `https://wa.me/?text=${encodedShare.value}`
    })

    // ---------- Helpers ----------
    const toast = (icon, title) => {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon,
        title,
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      })
    }

    async function copyComposed() {
      try {
        await navigator.clipboard.writeText(shareText.value)
        toast("success", "Copied details + link!")
      } catch {
        toast("error", "Could not copy. Please copy manually.")
      }
    }

    const canNativeShare = computed(() => {
      return typeof navigator !== 'undefined' && !!navigator.share
    })

    async function nativeShare() {
      if (!canNativeShare.value) return
      try {
        await navigator.share({
          title: plainTitle.value,
          text: shareText.value,
          url: rsvpUrl.value
        })
      } catch (err) {
        // User cancelled or share failed: silently ignore
      }
    }

    return {
      facebookUrl,
      linkedinUrl,
      whatsAppUrl,
      mapHref: safeMapHref,
      copyComposed,
      canNativeShare,
      nativeShare
    }
  }
})
</script>

<style scoped>
.cm-overlay{
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 1rem;
}
.cm-modal{
  width: 100%;
  max-width: 680px;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.25);
  overflow: hidden;
}
.cm-header, .cm-footer{
  padding: .9rem 1.25rem;
  background: #f8fafc;
  border-bottom: 1px solid rgba(15,23,42,0.06);
}
.cm-footer{
  border-top: 1px solid rgba(15,23,42,0.06);
  border-bottom: 0;
}
.cm-body{ padding: 1.25rem; background: #fff; }
.cm-link{ color:#0d6efd; text-decoration:underline; }
.cm-close{
  border: 0;
  background: transparent;
  font-size: 1.35rem;
  line-height: 1;
  color: #0f172a;
}
.cm-cover{ width: 88px; height: 88px; object-fit: cover; border-radius: .5rem; box-shadow:0 8px 18px rgba(15,23,42,0.15); }
.gap-2 { gap: .5rem; }

/* Responsive summary: stack on narrow screens */
.cm-summary{
  flex-wrap: wrap;
}
.cm-summary .cm-cover{
  margin-bottom: .5rem;
}
@media (min-width: 576px){
  .cm-summary{
    flex-wrap: nowrap;
  }
}

/* Action grid: 2 cols on phones, 3 on tablets, 4 on desktop */
.cm-actions{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .5rem;
}
.cm-actions > .btn{
  width: 100%;
  text-align: center;
  white-space: nowrap;
}
@media (min-width: 576px){
  .cm-actions{ grid-template-columns: repeat(3, 1fr); }
}
@media (min-width: 992px){
  .cm-actions{ grid-template-columns: repeat(4, 1fr); }
}

@media (max-width: 480px){
  .cm-modal {
    border-radius: 14px;
  }
  .cm-body {
    padding: 1rem;
  }
  .cm-actions {
    grid-template-columns: 1fr;
  }
}
</style>
